import { useState, useEffect } from 'react';
import { Grid, Box, Button, Select, MenuItem, FormControl, InputLabel } from '@mui/material';
import ValidatedTextField from '../../components/ValidatedTextField';
import { isMinLength2, isValidZip } from '../../../lib/validate';
import { US_STATES } from '../../../lib/states';
import { COUNTRIES } from '../../../lib/country';
import { OrderFormProps, saveOrder } from '../../lib/order';
import { fetchOrder } from '../../lib/order';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { useApp } from '../../components/AppContext';
import { BillingInformationRequest } from '@/src/types/order';
import { getToken } from '../../components/auth/AuthContext';
import { APIHOST } from '../../common';

export default function BillingInformationForm({
  orderId,
  back,
  onSave,
  type
}: OrderFormProps) {

  const[billingAddressee, setBillingAddressee] = useState('');
  const[billingStreet1, setBillingStreet1] = useState('');
  const[billingStreet2, setBillingStreet2] = useState('');
  const[billingCity, setBillingCity] = useState('');
  const[billingState, setBillingState] = useState('');
  const[billingZip, setBillingZip] = useState('');
  const[billingCountry, setBillingCountry] = useState('US');
  const[creditCardZipCode, setCreditCardZipCode] = useState('');
  const { setSnackbarInput } = useApp();

  const isFormValid = () => {
    return isMinLength2(billingAddressee) &&
           isMinLength2(billingStreet1) &&
           isMinLength2(billingCity) &&
           isValidZip(billingZip);
  }

  useEffect(() => {
    // Fetch order details on mount
    fetchOrder(orderId)
    .then(data => {
      if (!data) {
        console.error('No order data found');
        return;
      }
      setBillingAddressee(data.billingAddressee || '');
      setBillingStreet1(data.billingStreet1 || '');
      setBillingStreet2(data.billingStreet2 || '');
      setBillingCity(data.billingCity || '');
      setBillingState(data.billingState || '');
      setBillingZip(data.billingZip || '');
      setBillingCountry(data.billingCountry || 'US');
      setCreditCardZipCode(data.creditCardZipCode || '');
    })
    .catch(error => {
      console.error('Error fetching order:', error);
    });
  }, [orderId]);

  const handleSaveOrderClick = () => {
      if (!orderId) return;
  
      const body: BillingInformationRequest = {
        data: {
          billingAddressee,
          billingStreet1,
          billingStreet2,
          billingCity,
          billingState,
          billingZip,
          billingCountry,
          creditCardZipCode
      }};
  
      const authToken = getToken();
      if (!authToken) return false;
  
      fetch(`${APIHOST}/api/orders/${orderId}/billing-information`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify(body)
      })
      .then(response => {
        if (!response.ok) {
          throw new Error('Failed to save billing information');
        }
        setSnackbarInput({ message: 'Billing information saved successfully', severity: 'success' });
        if (onSave) {
          onSave();
        }
      })
      .catch(error => {
        console.error('Error saving billing information:', error);
        setSnackbarInput({ message: 'Error saving billing information', severity: 'error' });
      });
  };
  
  return (
  <Box>
    <Box sx={{ mt: 2 }}>
      <Grid container spacing={3}>
        <Grid size={{ xs: 12, md: 6}}>
          <ValidatedTextField
            validator={isMinLength2}
            fullWidth
            label="Billing Name"
            value={billingAddressee || ''}
            onChange={(e) => setBillingAddressee(e.target.value)}
            variant="outlined"
            size="small"
            required
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6}}>
          <ValidatedTextField
            validator={isMinLength2}
            fullWidth
            label="Street Address"
            value={billingStreet1 || ''}
            onChange={(e) => setBillingStreet1(e.target.value)}
            variant="outlined"
            size="small"
            required
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6}}>
          <ValidatedTextField
            validator={isMinLength2}
            fullWidth
            label="Street Address Line 2"
            value={billingStreet2 || ''}
            onChange={(e) => setBillingStreet2(e.target.value)}
            variant="outlined"
            size="small"
            placeholder="Apartment, suite, unit, building, floor, etc."
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6}}>
          <ValidatedTextField
            validator={isMinLength2}
            fullWidth
            label="City"
            value={billingCity || ''}
            onChange={(e) => setBillingCity(e.target.value)}
            variant="outlined"
            size="small"
            required
          />
        </Grid>
        <Grid size={{ xs: 12, md: 3}}>
          <FormControl fullWidth size="small" required>
            <InputLabel>State</InputLabel>
            <Select
              value={billingState || ''}
              onChange={(e) => setBillingState(e.target.value)}
              label="State"
            >
              {Object.entries(US_STATES).map(([code, name]) => (
                <MenuItem key={code} value={code}>
                  {name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid size={{ xs: 12, md: 3}}>
          <ValidatedTextField
            validator={isValidZip}
            fullWidth
            label="Postal Code"
            value={billingZip || ''}
            onChange={(e) => setBillingZip(e.target.value)}
            variant="outlined"
            size="small"
            required
            placeholder="12345"
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6}}>
          <FormControl fullWidth size="small" required>
            <InputLabel>Country</InputLabel>
            <Select
              value={billingCountry || 'US'}
              onChange={(e) => setBillingCountry(e.target.value)}
              label="Country"
            >
              {Object.entries(COUNTRIES).map(([code, name]) => (
                <MenuItem key={code} value={code}>
                  {name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid size={{ xs: 12, md: 6}}>
          <ValidatedTextField
            fullWidth
            label="Credit Card Zip Code"
            value={creditCardZipCode || ''}
            onChange={(e) => setCreditCardZipCode(e.target.value)}
            variant="outlined"
            size="small"
            placeholder="12345"
          />
        </Grid>
      </Grid>
    </Box>
      <Box>
        {back && <Button
          color="primary"
          size="small"
          startIcon={<ArrowBackIcon fontSize="small" />}
          sx={{px: 4, py: 0.5, mt: 2}}
          onClick={back}  >
          Back
        </Button>}
        <Button
          color="primary"
          size="small"
          variant="contained"
          startIcon={<ArrowForwardIcon fontSize="small" />}
          sx={{px: 4, py: 0.5, mt: 2}}
          onClick={handleSaveOrderClick}
          disabled={!isFormValid()}
        >
          {type === 'step' ? 'Next' : 'Save'}
        </Button>
      </Box>      
  </Box>
  );
}